import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Button from '../../shared/TailwindComponents/Button';
import Dropdown, { DropdownOption } from '../../shared/TailwindComponents/Dropdown';
import Badge from '../../shared/TailwindComponents/Badge';
import DateTimePicker from '../../shared/TailwindComponents/DatePicker';
import {
  PiPencilLight,
  PiCalendarBlankLight,
  PiClockCountdownLight,
  PiFlagLight,
  PiCircleDashedLight,
  PiSuitcaseSimpleLight,
  PiArrowSquareOutLight,
  PiCalendarCheckLight,
  PiClockClockwiseLight,
  PiUserLight,
  PiTextAlignLeftLight,
  PiScrollLight,
} from 'react-icons/pi';
import { formatDate, formatDateTime, parseServerDate } from '../../../utils/functions';
import RecentActivityCard from '../../shared/RecentActivityCard';
import { fetchTaskAuditLog } from '../../../store/actions/TasksActions';
import { useAppDispatch } from '../../../store';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, taskDetailsTabTourSteps } from '../../shared/FeatureTour';
import GlassCard from '../../shared/brand/GlassCard';
import { Field, FactTile, FieldGrid, MetaTile, Well } from '../../shared/brand/Field';
import {
  taskDueInfo,
  taskDueTone,
  taskPriorityColor,
  taskStatusColor,
  taskStatusLabel,
} from '../taskFacts';

interface TaskDetailsTabContentProps {
  taskDetails: any;
  taskUuid: string;
  userAccessLevel: 'edit' | 'view' | 'owner' | 'admin';
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  formik: any;
  hasChanges: boolean;
  setHasChanges: (changes: boolean) => void;
  onSubmit: () => void;
  taskStatus: string;
  /** Opens the matter picker side panel (link, move or unlink). */
  onChangeMatter?: () => void;
  onOpenAuditLog?: () => void;
}

/** A grey well whose fields render white (see `.surface-canvas`). */
const FIELD_GROUP = 'surface-canvas rounded-lg border border-hairline-soft bg-surface-sunken/70 p-4';

const TaskDetailsTabContent: React.FC<TaskDetailsTabContentProps> = ({
  taskDetails,
  taskUuid,
  userAccessLevel,
  isEditing,
  setIsEditing,
  formik,
  hasChanges,
  setHasChanges,
  onSubmit,
  taskStatus,
  onChangeMatter,
  onOpenAuditLog,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  // A task created from a contract obligation / renewal carries the contract in
  // its metadata - surface it as a proper "Linked contract" card (not a banner).
  const clm = (() => {
    let m = taskDetails?.metadata;
    if (typeof m === 'string') { try { m = JSON.parse(m); } catch { m = null; } }
    if (!m || !m.contract_uuid) return null;
    const label = m.source === 'clm_renewal' ? t('Renewal') : t('Obligation');
    return { uuid: m.contract_uuid as string, title: (m.contract_title as string) || t('Contract'), kind: label };
  })();

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'task-details-tab',
    steps: taskDetailsTabTourSteps,
    readyToShow: !!taskDetails,
    autoStartDelay: 600,
  });

  const handleFormChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
    setHasChanges(true);
  };

  const priorityOptions: DropdownOption[] = useMemo(() => [
    { value: 'low', label: t('Low') },
    { value: 'medium', label: t('Medium') },
    { value: 'high', label: t('High') },
  ], [t]);

  const linkedMatter: any = taskDetails?.matter?.uuid ? taskDetails.matter : null;
  const linkedMatterType: string | undefined =
    linkedMatter?.category_name || linkedMatter?.matter_type || linkedMatter?.type || undefined;
  const canChangeMatter =
    userAccessLevel !== 'view' && taskStatus !== 'completed' && taskStatus !== 'deleted';

  /* The status/priority/urgency derivations are shared with the page header
     and the context pane - see `../taskFacts`. Three copies is how the header
     came to say "amber" while the tile said "yellow". */
  const dueDateInfo = useMemo(
    () => taskDueInfo(formik.values.due_date, taskStatus, t),
    [formik.values.due_date, taskStatus, t]
  );
  const dueTone = taskDueTone(dueDateInfo);

  const createdByName = useMemo(() => {
    if (!taskDetails?.collaborators?.length) return null;
    const creator = taskDetails.collaborators.find((c: any) => c.role === 'creator');
    return creator?.name || null;
  }, [taskDetails?.collaborators]);

  return (
    <>
    {/*
      NO HEIGHT OF ITS OWN. This was `max-h-[calc(100vh-100px)]` with its own
      scroller - a guess at the height of the frame above it, nested inside
      the page's own `calc(100vh-150px)` guess, so the tab scrolled inside a
      scroller inside a scroller. The surface owns the height now and its
      canvas child claims the flex space; see `shared/surface/README`.
    */}
    <div className="details-canvas w-full">
      <div className="details-pad mx-auto w-full max-w-6xl px-6 py-8">
      <div className="details-body gap-6 items-start">

        {/* ── Left Column ── */}
        <div className="min-w-0 space-y-8">

          {/* ── Description Card ── */}
          <GlassCard
            data-tour-id="task-description-card"
            icon={<PiTextAlignLeftLight />}
            title={t('Description')}
            padding="lg"
            actions={
              !isEditing && userAccessLevel !== 'view' && (taskStatus !== 'completed' && taskStatus !== 'deleted') ? (
                <Button
                  onClick={() => setIsEditing(true)}
                  variant='primary'
                  size='small'
                  icon={<PiPencilLight className="h-3.5 w-3.5" />}
                >
                  {t('Edit')}
                </Button>
              ) : undefined
            }
          >
            {isEditing && userAccessLevel !== 'view' ? (
              <div className="space-y-4">
                {/*
                  WHITE FIELDS, TWO GROUPS. Each group is a grey well and
                  `surface-canvas` turns the field token white inside it, so
                  every input and dropdown reads white-on-grey (S5) instead of
                  grey-on-white glass. What the task says sits in one group,
                  when and how urgent in the other.
                */}
                <div className={clsx(FIELD_GROUP, 'space-y-4')}>
                <Textarea
                  label={t('Title')}
                  placeholder={t('Enter task title')}
                  value={formik.values.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  error={formik.touched.title && formik.errors.title as string}
                  className="w-full"
                  id="title"
                />
                <Textarea
                  label={t('Description')}
                  placeholder={t('Enter task description')}
                  value={formik.values.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  error={formik.touched.description && formik.errors.description as string}
                  className="w-full"
                  id="description"
                />
                </div>

                <div className={clsx(FIELD_GROUP, 'details-grid-2 gap-4')}>
                  <DateTimePicker
                    id="due_date"
                    label={t('Due date')}
                    selected={parseServerDate(formik.values.due_date)}
                    onChange={(date: Date | null) => handleFormChange('due_date', date ? date.toISOString() : '')}
                    placeholder={t('Select due date')}
                    dateFormat="MMM d, yyyy h:mm aa"
                    showTimeSelect={true}
                    timeIntervals={15}
                    required={false}
                    error={formik.touched.due_date && !!formik.errors.due_date}
                    helperText={formik.touched.due_date && formik.errors.due_date as string}
                    fullWidth={true}
                  />
                  <Dropdown
                    id="priority"
                    label={t('Priority')}
                    options={priorityOptions}
                    value={formik.values.priority}
                    onSelect={(option) => handleFormChange('priority', option.value)}
                    placeholder={t('Select priority')}
                    error={formik.touched.priority ? (formik.errors.priority as string) : undefined}
                    fullWidth={true}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t border-hairline-soft">
                  <Button
                    variant="neutral"
                    size="small"
                    onClick={() => {
                      setIsEditing(false);
                      setHasChanges(false);
                      formik.resetForm();
                    }}
                  >
                    {t('Cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => formik.handleSubmit()}
                    disabled={!hasChanges}
                  >
                    {t('Save changes')}
                  </Button>
                </div>
              </div>
            ) : formik.values.description ? (
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap max-w-prose">
                {formik.values.description}
              </p>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                {t('No description provided')}
              </p>
            )}
          </GlassCard>

          {/* ── Metadata Grid ── */}
          <div data-tour-id="task-metadata-grid">
            <FieldGrid measure="container" columns={4}>

              {/* Status */}
              <FactTile icon={<PiCircleDashedLight />} label={t('Status')}>
                <Badge color={taskStatusColor(taskStatus) as any}>
                  {taskStatusLabel(taskStatus, t)}
                </Badge>
              </FactTile>

              {/* Priority */}
              <FactTile icon={<PiFlagLight />} label={t('Priority')}>
                <Badge color={taskPriorityColor(formik.values.priority) as any}>
                  {formik.values.priority ? formik.values.priority.charAt(0).toUpperCase() + formik.values.priority.slice(1) : t('Medium')}
                </Badge>
              </FactTile>

              {/* Created */}
              <FactTile
                icon={<PiCalendarBlankLight />}
                label={t('Created')}
                value={taskDetails?.created_at ? formatDate(taskDetails.created_at) : undefined}
                emptyText="-"
              />

              {/* Due Date */}
              <GlassCard tone={dueTone}>
                <Field icon={<PiClockCountdownLight />} label={t('Due date')} tone={dueTone}>
                  {formik.values.due_date ? (
                    <div className="flex flex-col gap-1">
                      <p className={clsx(
                        'text-sm font-medium',
                        dueTone === 'danger' ? 'text-danger-600 dark:text-danger-400' :
                        dueTone === 'warning' ? 'text-warning-600 dark:text-warning-400' :
                        'text-gray-700 dark:text-gray-200',
                      )}>
                        {formatDateTime(formik.values.due_date)}
                      </p>
                      {dueDateInfo && (
                        <span className={clsx(
                          'text-[11px] font-medium',
                          dueTone === 'danger' && 'text-danger-500 dark:text-danger-400',
                          dueTone === 'warning' && 'text-warning-600 dark:text-warning-400',
                          dueTone === 'success' && 'text-success-600 dark:text-success-400',
                        )}>
                          {dueDateInfo.text}
                        </span>
                      )}
                    </div>
                  ) : null}
                </Field>
              </GlassCard>
            </FieldGrid>
          </div>

          {/* ── Activity Row ── */}
          <FieldGrid measure="container" columns={2}>
            {createdByName && (
              <MetaTile icon={<PiUserLight />} label={t('Created by')} value={createdByName} />
            )}

            {taskDetails?.updated_at && (
              <MetaTile icon={<PiClockClockwiseLight />} label={t('Last updated')} value={formatDateTime(taskDetails.updated_at)} />
            )}

            {taskDetails?.completed_at && (
              <MetaTile icon={<PiCalendarCheckLight />} label={t('Completed')} tone="success" value={formatDateTime(taskDetails.completed_at)} />
            )}
          </FieldGrid>
        </div>

        {/* ── Right Column ── */}
        <div className="details-side min-w-0 space-y-4">

          {/* ── Linked Contract Card (tasks promoted from a contract) ── */}
          {clm && (
            <GlassCard icon={<PiScrollLight />} title={t('Linked contract')}>
              <button
                onClick={() => history.push(`/contracts/${clm.uuid}`)}
                className="glass group relative w-full text-start rounded-lg p-4 hover:shadow-md transition-all flex flex-col gap-2"
              >
                <div className="flex items-start gap-2.5">
                  <PiScrollLight className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                  <h3 className="flex-1 text-sm text-gray-900 dark:text-gray-100 leading-snug line-clamp-2">{clm.title}</h3>
                  <PiArrowSquareOutLight className="flex-shrink-0 w-3.5 h-3.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
                </div>
                <Badge color="gray" className="w-fit capitalize">{clm.kind}</Badge>
              </button>
            </GlassCard>
          )}

          {/* ── Linked Matter Card ──
              A read-only card - title, type, a way there - and ONE button that
              opens the matter picker side panel (`linkTaskToMatter`), which is
              also where the task is unlinked. The inline Dropdown that used to
              sit here could neither search nor unlink. */}
          <GlassCard
            data-tour-id="task-linked-matter"
            icon={<PiSuitcaseSimpleLight />}
            title={t('Linked matter')}
            actions={
              canChangeMatter && onChangeMatter ? (
                <Button variant="neutral" size="small" onClick={onChangeMatter}>
                  {linkedMatter ? t('Change matter') : t('Link matter')}
                </Button>
              ) : undefined
            }
          >
            {linkedMatter ? (
              <Well className="flex items-start gap-3 p-3">
                <PiSuitcaseSimpleLight className="mt-0.5 h-4 w-4 flex-shrink-0 text-fg-muted" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-fg-strong leading-snug line-clamp-2">
                    {linkedMatter.title || taskDetails.matter_name || t('Untitled matter')}
                  </p>
                  {(linkedMatterType || linkedMatter.client?.name) && (
                    <p className="mt-0.5 text-xs text-fg-muted truncate">
                      {[linkedMatterType, linkedMatter.client?.name].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => history.push(`/matters/${linkedMatter.uuid}`)}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-fg-body hover:text-fg-strong transition-colors"
                  >
                    {t('Open matter')}
                    <PiArrowSquareOutLight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </Well>
            ) : (
              <p className="text-sm text-fg-muted">{t('No matter linked')}</p>
            )}
          </GlassCard>

          {/* ── Recent Activity ── */}
          <RecentActivityCard
            fetchEntries={async (limit) => {
              const res: any = await dispatch(fetchTaskAuditLog(taskUuid, 1, limit) as any);
              return res?.entries || [];
            }}
            onViewAll={onOpenAuditLog}
          />
        </div>
      </div>
      </div>
    </div>
    <FeatureTour
      steps={taskDetailsTabTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    </>
  );
};

export default TaskDetailsTabContent;
